"use client"

import React from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { cn } from "@/lib/utils"
import { CRMKPIGrid } from "@/components/shared/crm-ui"

// ── CRMErrorState ─────────────────────────────────────────────────────────────
// Full-page or inline error state with optional retry action.

export function CRMErrorState({
  title = "Ocurrió un error",
  description = "No se pudieron cargar los datos. Intentá de nuevo.",
  onRetry,
  className,
}: {
  title?: string
  description?: string
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center py-16 text-center gap-4",
        className,
      )}
    >
      <span
        className="flex items-center justify-center size-12 rounded-full bg-status-error-bg text-status-error"
        aria-hidden
      >
        <AlertTriangle className="size-6" />
      </span>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground max-w-xs">{description}</p>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-muted transition-colors"
        >
          <RefreshCw className="size-3.5" aria-hidden />
          Reintentar
        </button>
      )}
    </div>
  )
}

// ── CRMLoadingPage ────────────────────────────────────────────────────────────
// Full-page skeleton: animated header + optional KPI grid + optional table.

export function CRMLoadingPage({
  showKPIs = true,
  showTable = true,
  kpiCount = 4,
  tableRows = 6,
  tableCols = 5,
}: {
  showKPIs?: boolean
  showTable?: boolean
  kpiCount?: number
  tableRows?: number
  tableCols?: number
}) {
  return (
    <div
      className="space-y-6 animate-fade-in"
      aria-busy
      aria-label="Cargando contenido…"
    >
      {/* Page header skeleton */}
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-2">
          <div className="h-7 w-48 bg-muted/60 rounded animate-pulse" aria-hidden />
          <div className="h-4 w-64 bg-muted/60 rounded animate-pulse" aria-hidden />
        </div>
        <div className="h-9 w-28 bg-muted/60 rounded-lg animate-pulse hidden sm:block" aria-hidden />
      </div>

      {/* KPI grid skeleton */}
      {showKPIs && (
        <CRMKPIGrid
          items={Array.from({ length: kpiCount }, () => ({
            label: '',
            value: '',
            isLoading: true,
          }))}
        />
      )}

      {/* Filters skeleton */}
      <div className="flex gap-2" aria-hidden>
        <div className="h-9 flex-1 bg-muted/60 rounded-lg animate-pulse" />
        <div className="h-9 w-24 bg-muted/60 rounded-lg animate-pulse hidden sm:block" />
        <div className="h-9 w-24 bg-muted/60 rounded-lg animate-pulse hidden sm:block" />
      </div>

      {/* Table skeleton */}
      {showTable && (
        <div className="rounded-xl border border-border overflow-hidden" aria-hidden>
          {/* Table header */}
          <div className="bg-muted border-b border-border px-4 py-3 flex gap-6 sm:gap-12">
            {Array.from({ length: tableCols }).map((_, i) => (
              <div
                key={i}
                className="h-3 bg-muted-foreground/20 rounded animate-pulse"
                style={{ width: `${50 + (i * 17) % 40}px` }}
              />
            ))}
          </div>
          {/* Table rows */}
          <div className="divide-y divide-border">
            {Array.from({ length: tableRows }).map((_, i) => (
              <div key={i} className="px-4 py-3 flex gap-6 sm:gap-12 items-center">
                {Array.from({ length: tableCols }).map((_, j) => (
                  <div
                    key={j}
                    className="h-4 bg-muted/60 rounded animate-pulse"
                    style={{ width: `${60 + (j * 19 + i * 7) % 80}px` }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
